import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  Building2, LogOut, User, Home, UserPlus, Stethoscope,
  FilePlus2, Wallet, BarChart3, KeyRound, LayoutDashboard, Menu, X, Calculator,
} from 'lucide-react';

export default function AppHeader() {
  const { user, logout, isAdmin, isAccountant, isManagement, isManager, isMarketingExecutive, canCalculateReferral, canCreateReferral } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const canSeeReports = isAdmin || isAccountant;

  // Central nav list (rendered twice: desktop bar + mobile drawer)
  // Marketing execs get their own workspace with only that nav item + a logout
  const items = isMarketingExecutive ? [
    { to: '/marketing/dashboard', label: 'My Workspace', icon: LayoutDashboard, testid: 'nav-marketing-workspace-link' },
  ] : [
    (isAdmin || isManagement || isManager) && { to: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard, testid: 'nav-dashboard-link' },
    { to: '/staff/home', label: 'Home', icon: Home, testid: 'nav-home-link' },
    canCreateReferral && { to: '/staff/referrals/new', label: 'New Referral', icon: FilePlus2, testid: 'nav-new-referral-link' },
    { to: '/staff/referrals', label: 'Referrals & Payments', icon: Wallet, testid: 'nav-referrals-list-link' },
    canCalculateReferral && { to: '/manager/calculations', label: 'Referral Calculations', icon: Calculator, testid: 'nav-manager-calc-link' },
    canSeeReports && { to: '/admin/reports', label: 'Reports', icon: BarChart3, testid: 'nav-reports-link' },
    isAdmin && { to: '/admin/referral-doctors', label: 'Referral Doctors', icon: UserPlus, testid: 'nav-referral-doctors-link' },
    isAdmin && { to: '/admin/consultants', label: 'Consultants', icon: Stethoscope, testid: 'nav-consultants-link' },
  ].filter(Boolean);

  const desktopLinkClass = ({ isActive }) =>
    `group relative flex items-center justify-center w-10 h-10 rounded-lg transition-colors ${
      isActive
        ? 'bg-teal-500 text-slate-950 shadow-md shadow-teal-500/20'
        : 'text-slate-300 hover:text-white hover:bg-slate-800'
    }`;

  const mobileLinkClass = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
      isActive
        ? 'bg-teal-500 text-slate-950'
        : 'text-slate-200 hover:text-white hover:bg-slate-800'
    }`;

  const roleBadgeClasses = {
    admin: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    accountant: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
    reception: 'bg-sky-500/20 text-sky-300 border-sky-500/30',
    lab_staff: 'bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/30',
    pharmacist: 'bg-rose-500/20 text-rose-300 border-rose-500/30',
    management: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30',
    manager: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
    marketing_executive: 'bg-orange-500/20 text-orange-300 border-orange-500/30',
    staff: 'bg-teal-500/20 text-teal-300 border-teal-500/30',
  };

  return (
    <header className="sticky top-0 z-40 bg-slate-900/90 border-b border-slate-800 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        {/* Brand + Desktop Nav */}
        <div className="flex items-center gap-6 min-w-0">
          <div className="flex items-center space-x-3 shrink-0">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-teal-500 to-emerald-400 flex items-center justify-center text-slate-950 shadow-md shadow-teal-500/20">
              <Building2 className="w-5 h-5 stroke-[2.5]" />
            </div>
            <span className="text-base xl:text-lg font-bold tracking-tight text-white font-mono hidden md:flex items-center gap-2 whitespace-nowrap">
              <span className="text-[10px] font-sans text-teal-400 font-normal px-2 py-0.5 rounded bg-teal-950 border border-teal-800">RMS</span>
            </span>
          </div>

          {/* Desktop nav — icon-only on xl+ with tooltip label on hover */}
          <nav className="hidden xl:flex items-center gap-1" data-testid="app-main-nav">
            {items.map((it) => (
              <NavLink key={it.to} to={it.to} className={desktopLinkClass} data-testid={it.testid} title={it.label}>
                <it.icon className="w-4 h-4" />
                <span className="pointer-events-none absolute top-full mt-1.5 px-2 py-1 rounded-md bg-slate-950 border border-slate-700 text-[10px] font-medium text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-50">
                  {it.label}
                </span>
              </NavLink>
            ))}
          </nav>
        </div>

        {/* User Profile + Actions */}
        <div className="flex items-center gap-2 shrink-0">
          <div className="hidden sm:flex items-center gap-2 bg-slate-950/60 border border-slate-800/80 px-3 py-1.5 rounded-xl">
            <div className="w-8 h-8 rounded-lg bg-teal-500/20 border border-teal-500/30 flex items-center justify-center text-teal-300">
              <User className="w-4 h-4" />
            </div>
            <div className="hidden md:block text-left">
              <div data-testid="logged-in-staff-name" className="text-xs font-semibold text-white leading-tight">
                {user?.name || 'Staff User'}
              </div>
              <div className="text-[10px] text-slate-400 font-mono">@{user?.username}</div>
            </div>
            <span
              data-testid="current-user-role-badge"
              className={`text-[10px] font-semibold px-2 py-0.5 rounded-md uppercase tracking-wider border ${
                roleBadgeClasses[user?.role] || 'bg-teal-500/20 text-teal-300 border-teal-500/30'
              }`}
            >
              {user?.role}
            </span>
          </div>

          <button
            onClick={() => navigate('/profile/change-password')}
            data-testid="change-password-btn"
            className="flex items-center gap-1.5 px-2.5 py-2 text-xs font-medium text-slate-300 hover:text-white bg-slate-800 hover:bg-slate-700 border border-slate-700/60 rounded-xl transition-all"
            title="Change Password"
          >
            <KeyRound className="w-4 h-4" />
            <span className="hidden lg:inline">Password</span>
          </button>

          <button
            onClick={handleLogout}
            data-testid="staff-logout-btn"
            className="flex items-center gap-1.5 px-2.5 py-2 text-xs font-medium text-slate-300 hover:text-white bg-slate-800 hover:bg-red-950/60 hover:text-red-300 border border-slate-700/60 rounded-xl transition-all"
            title="Logout"
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden lg:inline">Logout</span>
          </button>

          {/* Mobile menu toggle — only < xl */}
          <button
            onClick={() => setMobileOpen((s) => !s)}
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
            className="xl:hidden flex items-center justify-center w-10 h-10 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700/60 text-slate-200"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile / tablet drawer */}
      {mobileOpen && (
        <div className="xl:hidden border-t border-slate-800 bg-slate-950/95 backdrop-blur-md" data-testid="mobile-nav-drawer">
          <nav className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex flex-col gap-1">
            {/* Compact user card at top of drawer for context */}
            <div className="sm:hidden mb-2 px-3 py-2.5 rounded-xl bg-slate-900/70 border border-slate-800 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-teal-500/20 border border-teal-500/30 flex items-center justify-center text-teal-300">
                <User className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-white truncate">{user?.name || 'Staff User'}</div>
                <div className="text-[10px] text-slate-400 font-mono truncate">@{user?.username}</div>
              </div>
              <span
                className={`text-[10px] font-semibold px-2 py-0.5 rounded-md uppercase tracking-wider border ${
                  roleBadgeClasses[user?.role] || 'bg-teal-500/20 text-teal-300 border-teal-500/30'
                }`}
              >
                {user?.role}
              </span>
            </div>

            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                className={mobileLinkClass}
                data-testid={`mobile-${it.testid}`}
                onClick={() => setMobileOpen(false)}
              >
                <it.icon className="w-4 h-4 shrink-0" />
                <span>{it.label}</span>
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
